import type { Box, BoxDraft, Item, ItemDraft, MoveProfileDraft } from '@easymoving/types';
import * as items from '../data/items';
import * as boxes from '../data/boxes';
import * as taxonomy from '../data/taxonomy';
import * as images from '../data/itemImages';
import * as moveProfile from '../data/moveProfile';
import type { Backend, Unsubscribe } from './types';

/**
 * Firestore/Storage backend, scoped to one signed-in user. Thin adapter: binds
 * the uid and delegates to the existing lib/data functions (unchanged).
 */
export class CloudBackend implements Backend {
  readonly mode = 'cloud' as const;
  constructor(private readonly uid: string) {}

  subscribeItems(cb: (i: Item[]) => void, onError?: (e: Error) => void): Unsubscribe {
    return items.subscribeItems(this.uid, cb, onError);
  }
  getItem(id: string) {
    return items.getItem(this.uid, id);
  }
  createItem(draft: ItemDraft) {
    return items.createItem(this.uid, draft);
  }
  updateItem(id: string, patch: Partial<Omit<Item, 'id' | 'createdAt'>>) {
    return items.updateItem(this.uid, id, patch);
  }
  deleteItem(id: string) {
    return items.deleteItem(this.uid, id);
  }
  duplicateItem(id: string) {
    return items.duplicateItem(this.uid, id);
  }

  uploadPrimaryImage(itemId: string, file: File) {
    return images.uploadPrimaryImage(this.uid, itemId, file);
  }
  clearPrimaryImage(itemId: string) {
    return images.clearPrimaryImage(this.uid, itemId);
  }
  deleteItemImages(itemId: string) {
    return images.deleteItemImages(this.uid, itemId);
  }
  addImage(itemId: string, file: File) {
    return images.addImage(this.uid, itemId, file);
  }
  removeImage(itemId: string, storagePath: string) {
    return images.removeImage(this.uid, itemId, storagePath);
  }
  setPrimaryImage(itemId: string, storagePath: string) {
    return images.setPrimaryImage(this.uid, itemId, storagePath);
  }

  subscribeBoxes(cb: (b: Box[]) => void, onError?: (e: Error) => void): Unsubscribe {
    return boxes.subscribeBoxes(this.uid, cb, onError);
  }
  getBox(id: string) {
    return boxes.getBox(this.uid, id);
  }
  createBox(draft: BoxDraft) {
    return boxes.createBox(this.uid, draft);
  }
  updateBox(id: string, patch: Partial<Omit<Box, 'id' | 'createdAt'>>) {
    return boxes.updateBox(this.uid, id, patch);
  }
  deleteBox(id: string) {
    return boxes.deleteBox(this.uid, id);
  }

  subscribeCustomRooms(cb: Parameters<Backend['subscribeCustomRooms']>[0]) {
    return taxonomy.subscribeCustomRooms(this.uid, cb);
  }
  createCustomRoom(name: string) {
    return taxonomy.createCustomRoom(this.uid, name);
  }
  renameCustomRoom(id: string, newName: string, affectedItemIds: string[]) {
    return taxonomy.renameCustomRoom(this.uid, id, newName, affectedItemIds);
  }
  deleteCustomRoom(id: string, affectedItemIds: string[]) {
    return taxonomy.deleteCustomRoom(this.uid, id, affectedItemIds);
  }

  subscribeCustomCategories(cb: Parameters<Backend['subscribeCustomCategories']>[0]) {
    return taxonomy.subscribeCustomCategories(this.uid, cb);
  }
  createCustomCategory(name: string) {
    return taxonomy.createCustomCategory(this.uid, name);
  }
  renameCustomCategory(id: string, newName: string, affectedItemIds: string[]) {
    return taxonomy.renameCustomCategory(this.uid, id, newName, affectedItemIds);
  }
  deleteCustomCategory(id: string, affectedItemIds: string[]) {
    return taxonomy.deleteCustomCategory(this.uid, id, affectedItemIds);
  }

  getMoveProfile() {
    return moveProfile.getMoveProfile(this.uid);
  }
  subscribeMoveProfile(cb: Parameters<Backend['subscribeMoveProfile']>[0]) {
    return moveProfile.subscribeMoveProfile(this.uid, cb);
  }
  saveMoveProfile(patch: MoveProfileDraft) {
    return moveProfile.saveMoveProfile(this.uid, patch);
  }
}
